import { useMemo } from 'react'
import type { ServiceCardState, CatalogData, QuoteLine, QuoteResult } from '../types'
import type { DiscountCategory } from '@/types'

const ODD_COLORS = [
  '#FF9D76',
  '#76F1FF',
  '#C176FF',
  '#FFE176',
  '#FF7676',
  '#76FF9D',
  '#9D76FF',
  '#76FFE1',
  '#FFB376',
  '#B376FF',
]

const DISCOUNT_PERCENT: Record<DiscountCategory, number> = {
  none: 0,
  student: 10,
  nonprofit: 10,
  odd_artist: 20,
}

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

function isInquiryOffering(pricingType: string): boolean {
  return pricingType === 'starting_at' || pricingType === 'range' || pricingType === 'contact'
}

function formatInquiryPrice(offering: CatalogData['offerings'][number]): string {
  const { pricingType, price, priceMax } = offering
  if (pricingType === 'contact') return 'TBD'
  if (pricingType === 'range' && price !== undefined && priceMax !== undefined)
    return `${fmt.format(price)}–${fmt.format(priceMax)}`
  if (pricingType === 'starting_at' && price !== undefined)
    return `From ${fmt.format(price)}`
  return 'TBD'
}

export function computeServiceBaseCost(card: ServiceCardState, catalog: CatalogData): number {
  const offering = catalog.offerings.find((o) => o.id === card.selectedOfferingId)
  if (!offering) return 0

  let rate = offering.basePrice ?? 0
  if (offering.hasComplexity && offering.complexityBase) {
    rate = offering.complexityBase[card.complexity] ?? offering.basePrice ?? 0
  }

  switch (offering.priceUnit) {
    case 'flat':
      return rate
    case 'perHour': {
      const setup = offering.setupTime ?? 0
      return rate * (card.hours + setup)
    }
    case 'perMinute':
      return rate * card.filmLengthMinutes
    case 'perHeadshot':
      return rate * card.headshots
    default:
      return 0
  }
}

export function computeAddonCost(addon: ServiceCardState['addons'][0], card: ServiceCardState): number {
  if (addon.selectionMode === 'boolean' && !addon.boolValue) return 0
  if (addon.selectionMode === 'integer' && addon.intValue <= 0) return 0
  if (addon.selectionMode === 'string') return 0 // informational only

  const qty = addon.selectionMode === 'integer' ? addon.intValue : 1

  switch (addon.pricingMode) {
    case 'flat':
      return addon.rate * qty
    case 'perHour':
      return addon.rate * qty * card.hours
    case 'perMinute':
      return addon.rate * qty * card.filmLengthMinutes
    default:
      return 0
  }
}

export function useQuoteCalculator(
  cards: ServiceCardState[],
  catalog: CatalogData | null,
  discountCategory: DiscountCategory
): QuoteResult {
  return useMemo(() => {
    const empty: QuoteResult = {
      lines: [],
      subtotal: 0,
      discountAmount: 0,
      grandTotal: 0,
      formattedTotal: '$0.00',
      hasInquiryItems: false,
    }

    if (!catalog) return empty

    const lines: QuoteLine[] = []
    let colorIdx = 0
    let subtotal = 0
    let hasInquiry = false

    for (const card of cards) {
      if (!card.selectedOfferingId) continue

      const offering = catalog.offerings.find((o) => o.id === card.selectedOfferingId)
      if (!offering) continue

      if (isInquiryOffering(offering.pricingType)) {
        hasInquiry = true
        lines.push({
          label: offering.title,
          amount: 0,
          color: ODD_COLORS[colorIdx % ODD_COLORS.length] ?? '#FF9D76',
          isInquiry: true,
          displayPrice: formatInquiryPrice(offering),
        })
        colorIdx++
        continue
      }

      const baseCost = computeServiceBaseCost(card, catalog)
      subtotal += baseCost

      lines.push({
        label: offering.title,
        amount: baseCost,
        color: ODD_COLORS[colorIdx % ODD_COLORS.length] ?? '#FF9D76',
      })
      colorIdx++

      for (const addon of card.addons) {
        const addonCost = computeAddonCost(addon, card)
        if (addonCost === 0) continue
        subtotal += addonCost
        lines.push({
          label: addon.label,
          amount: addonCost,
          color: ODD_COLORS[colorIdx % ODD_COLORS.length] ?? '#76F1FF',
          isAddon: true,
        })
        colorIdx++
      }
    }

    const discountPct = DISCOUNT_PERCENT[discountCategory]
    const discountAmount = (subtotal * discountPct) / 100
    const grandTotal = subtotal - discountAmount

    let formattedTotal: string
    if (hasInquiry && grandTotal > 0)  formattedTotal = `${fmt.format(grandTotal)} + custom quote`
    else if (hasInquiry)               formattedTotal = 'Custom quote required'
    else                               formattedTotal = fmt.format(grandTotal)

    return {
      lines,
      subtotal,
      discountAmount,
      grandTotal,
      formattedTotal,
      hasInquiryItems: hasInquiry,
    }
  }, [cards, catalog, discountCategory])
}
